import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Mic, Square, Loader2, X, Check } from 'lucide-react';
import { pollTranscriptionStatus } from '../utils/voiceTranscription';

interface VoiceRecorderProps {
  /** API endpoint for transcription (default: /api/voice/transcribe/) */
  endpoint?: string;
  /** Called when transcription is complete */
  onTranscriptionComplete: (result: TranscriptionResult) => void;
  /** Called when recording is cancelled */
  onCancel?: () => void;
  /** Additional context for transcription */
  context?: string;
  /** Whether to request summarization */
  summarize?: boolean;
  /** Additional form data to send with the audio */
  additionalData?: Record<string, string>;
  /** Compact mode for inline use */
  compact?: boolean;
  /** Custom placeholder text */
  placeholder?: string;
  /** Button variant */
  variant?: 'default' | 'icon' | 'inline';
}

interface TranscriptionResult {
  text: string;
  summary?: string;
  action_items?: string[];
  duration?: number;
}

type RecordingState = 'idle' | 'recording' | 'processing';

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  endpoint = '/api/voice/transcribe/',
  onTranscriptionComplete,
  onCancel,
  context,
  summarize = false,
  additionalData,
  compact = false,
  placeholder = 'Record a voice note',
  variant = 'default'
}) => {
  const [state, setState] = useState<RecordingState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [permissionDenied, setPermissionDenied] = useState(false);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopRecording();
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    setPermissionDenied(false);
    audioChunksRef.current = [];

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // Use webm format (best browser support)
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });

      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Audio is collected in chunks, will be processed in stopRecording
      };

      mediaRecorder.start();
      setState('recording');
      setRecordingDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration((d) => d + 1);
      }, 1000);

    } catch (err: unknown) {
      console.error('Failed to start recording:', err);
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setPermissionDenied(true);
        setError('Microphone access denied. Please allow microphone access in your browser settings.');
      } else {
        setError('Failed to start recording. Please check your microphone.');
      }
    }
  }, []);

  const stopRecording = useCallback(async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const handleStopAndSubmit = useCallback(async () => {
    // Stop recording first
    await stopRecording();
    setState('processing');

    // Wait a moment for all audio chunks to be collected
    await new Promise(resolve => setTimeout(resolve, 100));

    // Create audio blob
    const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

    if (audioBlob.size === 0) {
      setError('No audio recorded. Please try again.');
      setState('idle');
      return;
    }

    // Prepare form data
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    if (context) {
      formData.append('context', context);
    }

    if (summarize) {
      formData.append('summarize', 'true');
    }

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    try {
      const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/api\/?$/, '');

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Transcription failed');
      }

      const initial = await response.json();
      const finalResult = initial?.job_id
        ? await pollTranscriptionStatus(API_BASE, initial.status_url || `/api/voice/transcribe/status/${initial.job_id}/`)
        : initial;

      onTranscriptionComplete(finalResult);
      setState('idle');
      setRecordingDuration(0);

    } catch (err) {
      console.error('Transcription error:', err);
      setError(err instanceof Error ? err.message : 'Failed to transcribe audio');
      setState('idle');
    }
  }, [stopRecording, endpoint, context, summarize, additionalData, onTranscriptionComplete]);

  const handleCancel = useCallback(() => {
    stopRecording();
    setState('idle');
    setRecordingDuration(0);
    setError(null);
    onCancel?.();
  }, [stopRecording, onCancel]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Icon-only button for inline use
  if (variant === 'icon') {
    return (
      <div style={styles.iconContainer}>
        {state === 'idle' && (
          <button
            onClick={startRecording}
            style={styles.iconButton}
            title={placeholder}
            type="button"
          >
            <Mic size={20} />
          </button>
        )}

        {state === 'recording' && (
          <div style={styles.iconRecordingRow}>
            <div style={styles.recordingIndicator}>
              <div style={styles.recordingDot} />
              <span style={styles.recordingTime}>{formatDuration(recordingDuration)}</span>
            </div>
            <button
              onClick={handleCancel}
              style={{ ...styles.iconButton, ...styles.iconButtonCancel }}
              title="Cancel"
              type="button"
            >
              <X size={18} />
            </button>
            <button
              onClick={handleStopAndSubmit}
              style={{ ...styles.iconButton, ...styles.iconButtonSubmit }}
              title="Done"
              type="button"
            >
              <Check size={18} />
            </button>
          </div>
        )}

        {state === 'processing' && (
          <div style={styles.processingIndicator}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        )}

        {error && (
          <div style={styles.iconError} title={error}>!</div>
        )}

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Compact inline version
  if (compact) {
    return (
      <div style={styles.compactContainer}>
        {state === 'idle' && (
          <button
            onClick={startRecording}
            style={styles.compactButton}
            type="button"
          >
            <Mic size={18} />
            <span>Voice note</span>
          </button>
        )}

        {state === 'recording' && (
          <div style={styles.compactRecording}>
            <div style={styles.recordingDot} />
            <span style={styles.compactRecordingText}>
              Recording... {formatDuration(recordingDuration)}
            </span>
            <button
              onClick={handleCancel}
              style={styles.compactCancelButton}
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleStopAndSubmit}
              style={styles.compactDoneButton}
              type="button"
            >
              Done
            </button>
          </div>
        )}

        {state === 'processing' && (
          <div style={styles.compactProcessing}>
            <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Transcribing...</span>
          </div>
        )}

        {error && <div style={styles.compactError}>{error}</div>}

        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </div>
    );
  }

  // Full default version
  return (
    <div style={styles.container}>
      {/* Idle State - Ready to record */}
      {state === 'idle' && (
        <div style={styles.idleContainer}>
          <button
            onClick={startRecording}
            style={styles.recordButton}
            type="button"
          >
            <Mic size={24} />
            <span>{placeholder}</span>
          </button>

          {permissionDenied && (
            <p style={styles.permissionText}>
              Microphone access required. Check browser settings.
            </p>
          )}
        </div>
      )}

      {/* Recording State */}
      {state === 'recording' && (
        <div style={styles.recordingContainer}>
          <div style={styles.recordingHeader}>
            <div style={styles.recordingDot} />
            <span style={styles.recordingLabel}>Recording</span>
            <span style={styles.recordingDuration}>{formatDuration(recordingDuration)}</span>
          </div>

          <div style={styles.waveformPlaceholder}>
            <div style={styles.waveBar} />
            <div style={{ ...styles.waveBar, height: '24px' }} />
            <div style={{ ...styles.waveBar, height: '32px' }} />
            <div style={{ ...styles.waveBar, height: '20px' }} />
            <div style={{ ...styles.waveBar, height: '28px' }} />
          </div>

          <div style={styles.recordingActions}>
            <button
              onClick={handleCancel}
              style={styles.cancelButton}
              type="button"
            >
              <X size={20} />
              Cancel
            </button>
            <button
              onClick={handleStopAndSubmit}
              style={styles.stopButton}
              type="button"
            >
              <Square size={20} />
              Done
            </button>
          </div>
        </div>
      )}

      {/* Processing State */}
      {state === 'processing' && (
        <div style={styles.processingContainer}>
          <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#2D5A27' }} />
          <span style={styles.processingText}>Transcribing your voice note...</span>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{error}</p>
          <button
            onClick={() => setError(null)}
            style={styles.dismissButton}
            type="button"
          >
            Dismiss
          </button>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes wave {
          0%, 100% { transform: scaleY(1); }
          50% { transform: scaleY(1.5); }
        }
      `}</style>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    width: '100%',
  },

  // Idle state
  idleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  recordButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 20px',
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    border: 'none',
    borderRadius: '24px',
    fontSize: '15px',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: '44px',
    transition: 'background-color 0.15s ease',
  },
  permissionText: {
    fontSize: '13px',
    color: '#dc2626',
    margin: 0,
    textAlign: 'center',
  },

  // Recording state
  recordingContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fef2f2',
    borderRadius: '12px',
    border: '1px solid #fecaca',
  },
  recordingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  recordingDot: {
    width: '10px',
    height: '10px',
    backgroundColor: '#dc2626',
    borderRadius: '50%',
    animation: 'pulse 1s ease-in-out infinite',
  },
  recordingLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#dc2626',
  },
  recordingDuration: {
    fontSize: '14px',
    color: '#7f1d1d',
    marginLeft: 'auto',
    fontFamily: 'monospace',
  },
  waveformPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '4px',
    height: '40px',
  },
  waveBar: {
    width: '4px',
    height: '16px',
    backgroundColor: '#dc2626',
    borderRadius: '2px',
    animation: 'wave 0.5s ease-in-out infinite',
    animationDelay: '0.1s',
  },
  recordingActions: {
    display: 'flex',
    gap: '12px',
    justifyContent: 'center',
  },
  cancelButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px',
    backgroundColor: '#ffffff',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: '44px',
  },
  stopButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 20px',
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: '44px',
  },

  // Processing state
  processingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    padding: '24px',
  },
  processingText: {
    fontSize: '14px',
    color: '#6b7280',
  },

  // Error
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  },
  errorText: {
    fontSize: '14px',
    color: '#dc2626',
    margin: 0,
    textAlign: 'center',
  },
  dismissButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    color: '#dc2626',
    border: '1px solid #dc2626',
    borderRadius: '6px',
    fontSize: '13px',
    cursor: 'pointer',
  },

  // Compact variant
  compactContainer: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  compactButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    cursor: 'pointer',
    minHeight: '36px',
  },
  compactRecording: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: '#fef2f2',
    borderRadius: '8px',
    border: '1px solid #fecaca',
  },
  compactRecordingText: {
    fontSize: '13px',
    color: '#dc2626',
    fontWeight: 500,
  },
  compactCancelButton: {
    padding: '4px 10px',
    backgroundColor: '#ffffff',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  compactDoneButton: {
    padding: '4px 10px',
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: 500,
    cursor: 'pointer',
  },
  compactProcessing: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    color: '#6b7280',
    fontSize: '13px',
  },
  compactError: {
    fontSize: '13px',
    color: '#dc2626',
    padding: '8px 12px',
  },

  // Icon variant
  iconContainer: {
    display: 'inline-flex',
    alignItems: 'center',
    position: 'relative',
  },
  iconButton: {
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '50%',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  iconButtonCancel: {
    backgroundColor: '#ffffff',
    color: '#6b7280',
    width: '32px',
    height: '32px',
  },
  iconButtonSubmit: {
    backgroundColor: '#2D5A27',
    color: '#ffffff',
    border: 'none',
    width: '32px',
    height: '32px',
  },
  iconRecordingRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  recordingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '4px 8px',
    backgroundColor: '#fef2f2',
    borderRadius: '12px',
  },
  recordingTime: {
    fontSize: '12px',
    color: '#dc2626',
    fontFamily: 'monospace',
  },
  processingIndicator: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '36px',
    height: '36px',
    color: '#2D5A27',
  },
  iconError: {
    position: 'absolute',
    top: '-4px',
    right: '-4px',
    width: '16px',
    height: '16px',
    backgroundColor: '#dc2626',
    color: '#ffffff',
    borderRadius: '50%',
    fontSize: '10px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

export default VoiceRecorder;
